import { useEffect, useState } from 'react'
import { Modal } from '../../../components/UI/Modal/Modal'
import CustomCheckbox from 'components/Checkbox/checkbox'
import { GoogleButton, OutlookButton } from './ProviderButtons'

interface AddAccountModalProps {
  open: boolean
  onClose: () => void
  onContinue: (provider: string, features: string[], isShared: boolean) => void
}

export function AddAccountModal({
  open,
  onClose,
  onContinue,
}: AddAccountModalProps) {
  const [syncEmail, setSyncEmail] = useState(false)
  const [syncCalendar, setSyncCalendar] = useState(false)
  const [isShared, setIsShared] = useState(false)

  useEffect(() => {
    if (!open) {
      setSyncEmail(false)
      setSyncCalendar(false)
      setIsShared(false)
    }
  }, [open])

  const canContinue = syncEmail || syncCalendar

  const handleContinue = (provider: string) => {
    if (!canContinue) return
    const features: string[] = []
    if (syncEmail) features.push('email')
    if (syncCalendar) features.push('calendar')
    onContinue(provider, features, isShared)
  }

  return (
    <Modal
      open={open}
      onOpenChange={(isOpen) => !isOpen && onClose()}
      hideCancelButton
      title={
        <div className="flex flex-col gap-1">
          <span className="text-xs font-light tracking-[0.03em] text-fg">
            INTEGRATIONS
          </span>
          <span className="text-xl font-semibold text-fg leading-7">
            Add New Account
          </span>
        </div>
      }
      description="Edit your default account preferences, then select your provider to add a new account to First Bite. You can always change preferences later."
    >
      <div className="flex flex-col gap-4">
        {/* Email Preferences section */}
        <div className="flex flex-col gap-[6px]">
          <h3 className="text-sm font-medium text-fg leading-5">
            Email Preferences
          </h3>
          <div className="flex items-center gap-2">
            <CustomCheckbox
              id="sync_email"
              checked={syncEmail}
              onChange={(e) => setSyncEmail(e.target.checked)}
              value={syncEmail ? 'true' : 'false'}
            />
            <label
              htmlFor="sync_email"
              className="text-xs font-medium text-fg leading-5"
            >
              Sync email to First Bite
            </label>
          </div>
          {syncEmail && (
            <div className="flex items-center gap-2">
              <CustomCheckbox
                id="is_shared_new"
                checked={isShared}
                onChange={(e) => setIsShared(e.target.checked)}
                value={isShared ? 'true' : 'false'}
              />
              <label
                htmlFor="is_shared_new"
                className="text-xs font-medium text-fg leading-5"
              >
                Share email visibility with my team
              </label>
            </div>
          )}
        </div>

        {/* Calendar Preferences section */}
        <div className="flex flex-col gap-[6px]">
          <h3 className="text-sm font-medium text-fg leading-5">
            Calendar Preferences
          </h3>
          <p className="text-xxs text-fg-secondary leading-[140%]">
            This is a one-way sync: First Bite-created events will sync to your
            calendar, but Outlook- or Google-created events will NOT appear in
            First Bite.
          </p>
          <div className="flex items-center gap-2">
            <CustomCheckbox
              id="sync_calendar"
              checked={syncCalendar}
              onChange={(e) => setSyncCalendar(e.target.checked)}
              value={syncCalendar ? 'true' : 'false'}
            />
            <label
              htmlFor="sync_calendar"
              className="text-xs font-medium text-fg leading-5"
            >
              Integrate calendar with First Bite
            </label>
          </div>
        </div>

        {/* Provider buttons */}
        <div className="flex flex-row gap-2">
          <GoogleButton
            className="flex-1"
            disabled={!canContinue}
            onClick={() => handleContinue('google')}
          />
          <OutlookButton
            className="flex-1"
            disabled={!canContinue}
            onClick={() => handleContinue('microsoft')}
          />
        </div>
      </div>
    </Modal>
  )
}
